/**
 * 搜索历史记录
 * 
 * @Author: Focci
 * @Date: 2023-09-29 14:56:54
 * @Last Modified by: Focci
 * @Last Modified time: 2023-09-29 14:56:54
 */

'use client'

import PropTypes from 'prop-types'
import Link from 'next/link'
import SVGSearchBlock from '@img/search-block.svg'
import PropertyIcons from '@comp/page/common/card/property/PropertyIcons'
import useSearchRecording, { getSearchTypeName } from '@hook/useSearchRecording'
import { isArray, isFunction, map, size } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { 
  getHistoryName, getHistoryPropertyIconData, getHistoryPriceLabel 
} from '@comp/page/home/search-recording/HistoryCard'
import SuggestionScoff from './SuggestionScoff'

function HistoryRecordingItem({
  chip,
  onClick = () => {},
  Tag = Link
}) {
  const th = useTranslations('home')
  const ts = useTranslations('search')
  const ta = useTranslations('agent')
  
  const { data } = chip || {}
  const { name } = useMemo(() => getHistoryName(chip, th), [chip, th])
  const subtypeName = useMemo(() => getSearchTypeName(data, ts, ta), [data, ta, ts])
  const priceLabel = useMemo(() => getHistoryPriceLabel(chip), [chip])
  const propertyIconData = useMemo(() => getHistoryPropertyIconData(data), [data])
  const categoryLabel = useMemo(() => {
    const { ptnCategory } = data || {}
    const tmp = isArray(ptnCategory) ? map(ptnCategory, 'label') : []
      
    return tmp.join(', ')
  }, [data])

  const handleClick = useCallback(() => onClick(chip), [chip, onClick])

  return (
    <Tag 
      href={chip.url} 
      key={chip.id}
      className="flex items-center gap-2 hover:bg-cf8 w-full p-1 cursor-pointer"
      onClick={handleClick}
    >
      <div className="shrink-0">
        <SVGSearchBlock />
      </div>
      <div>
        <div className="text-f.7 font-medium text-c18 capitalize line-clamp-1">
          {name}
        </div>
        <div className="text-f.6 text-helper flex items-center gap-x-2">
          <span>{subtypeName}</span>
          {categoryLabel && (
            <>
              <span className="h-1 w-1 bg-cd8 rounded-full" />
              <span>{categoryLabel}</span>
            </>
          )}
          {priceLabel && (
            <>
              <span className="h-1 w-1 bg-cd8 rounded-full" />
              <span>{priceLabel}</span>
            </>
          )}
          {size(propertyIconData) > 0 && (
            <>
              <span className="h-1 w-1 bg-cd8 rounded-full" />
              <PropertyIcons iconWidth=".7rem" data={propertyIconData} />
            </>
          )}
        </div>
      </div>
    </Tag>
  )
}

export default function HistoryRecording({
  type,
  handleItem
}) {
  const ts = useTranslations('search')
  const { records, headRecord } = useSearchRecording()
  const lists = records.filter((f) => f.type === type).slice(0, 5)

  const tag = isFunction(handleItem) ? 'div' : Link
  const handleClick = useCallback((d) => {
    headRecord(d)
    if(isFunction(handleItem)) {
      handleItem({
        targetData: { type: d.type, searchParam: d.data }
      })
    }
  }, [handleItem, headRecord])

  if(lists.length === 0) {
    return null
  }
  
  return (
    <SuggestionScoff label={ts('historyRecord')}>
      <div className="flex flex-col w-full">
        {map(lists, (chip) => chip.url && (
          <HistoryRecordingItem
            chip={chip}
            Tag={tag}
            key={chip.id}
            onClick={handleClick}
            handleItem={handleItem}
          />
        ))}
      </div>
    </SuggestionScoff>
  )
}

HistoryRecording.propTypes = {
  type: PropTypes.string,
}
